import { useMutation, useQueries } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { DataQueryParams } from 'common/src/query-params/DataQueryParams';
import { SingleOperators } from 'common/src/query-params/operators';
import { NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import {
  ICreateAccountDto,
  IUpdateAccountDto,
} from 'common/src/types/account.dtos';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { IAccount, IPage, IUtility } from 'common/src/types/interfaces';

import {
  addAccount,
  getAccounts,
  updateAccount,
} from 'portal/src/api/AccountsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import { getUtilities } from 'portal/src/api/UtilitiesService';
import useAuth from 'portal/src/auth/useAuth';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import { prepareFormPayload } from 'portal/src/components/common/forms/handleFormData';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import { BUTTON_TEXT } from 'portal/src/constants/button-text';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import {
  AccountFormValues,
  defaultValues,
  getFields,
} from './AccountForm.fields';

import AssociatedMeters from '../../Meters/AssociatedMeters';
import HistoricalValuesDialogTable from '../HistoricalValues/HistoricalValuesDialogTable';
import { useHistoricalValues } from '../HistoricalValues/useHistoricalValues';

const AccountForm = ({
  entity = {},
  handleBackAction,
}: {
  entity?: Partial<IAccount>;
  handleBackAction: () => any;
}) => {
  const { featureChecker, getUser, hasReadOnlyAccess, hasSystemAdminAccess } =
    useAuth();
  const { organizationId } = getUser();
  const navigate = useNavigate();
  const snackbarHandler = useSnackbarHandler();
  const isUpdatingAccount = !!entity.id;

  const excludeEntityQueryParams: DataQueryParams = entity.id
    ? {
        filters: [
          {
            field: 'id',
            operator: SingleOperators.NOT_EQUAL_TO,
            value: entity.id,
          },
        ],
      }
    : {};

  const [{ data: accounts }, { data: utilities }] = useQueries({
    queries: [
      {
        queryKey: [QUERY_KEYS.accounts],
        queryFn: async () => await getAccounts(excludeEntityQueryParams),
        initialData: { data: [] } as IPage<IAccount>,
      },
      {
        queryKey: [QUERY_KEYS.utilities],
        queryFn: async () => await getUtilities(),
        initialData: { data: [] } as IPage<IUtility>,
      },
    ],
  });

  const masterAccounts = accounts.data?.filter((account) => account.isMaster);

  const isDeactivated = entity.id && !entity.isActive;
  const defaults = defaultValues(entity);

  const {
    control,
    formState: { isDirty, dirtyFields },
    getValues,
    handleSubmit,
    setError,
    setValue,
  } = useForm({ values: defaults });
  const [selectedCountry, isMaster] = useWatch({
    name: ['country', 'isMaster'],
    control,
  });
  const { getSnackbarApiErrorMessage } = useFormControlError({
    getValues,
    setError,
  });

  const historyConfigs = useHistoricalValues(
    defaults.rateSchedule,
    entity.rateScheduleHistoricalValues,
    entity.createdAt,
  );

  const hasActiveTemplate =
    !!entity.activeAllocationTemplate ||
    !!entity.masterAccount?.activeAllocationTemplate;

  // Do not memoize the result of this call
  // If we use `entity.id` as a dependency, we get this error from `react-hooks/preserve-manual-memoization`:
  // "This dependency may be mutated later, which could cause the value to change unexpectedly"
  // `entity` and it's properties do not appear to be mutated
  //
  // This is not a terribly expensive operation and React Compiler may memoize it for us
  const formFieldsConfig = getFields(
    accounts.data,
    utilities.data,
    masterAccounts,
    hasSystemAdminAccess(),
    !isMaster,
    historyConfigs.handleValueChange,
    selectedCountry,
    setValue,
    hasActiveTemplate,
    entity.id ? historyConfigs.handleOpen : null,
    featureChecker.productFeatureEnabled(ProductFeatures.allocations),
  );

  const formatPayload = (payload: AccountFormValues) => {
    const formattedFields = {
      ...prepareFormPayload(payload, dirtyFields),
    };

    return {
      ...formattedFields,
      ...(formattedFields.rateSchedule !== undefined || historyConfigs.isDirty
        ? {
            rateScheduleHistoricalValues: historyConfigs.formatPayload(),
          }
        : {}),
    };
  };

  const { mutate: accountMutation, isPending } = useMutation<
    void,
    Error,
    AccountFormValues
  >({
    mutationFn: async (payload) => {
      const formattedPayload = formatPayload(payload);

      if (isUpdatingAccount) {
        await updateAccount(
          entity.id,
          formattedPayload as unknown as IUpdateAccountDto,
        );
        return;
      }

      await addAccount({
        ...formattedPayload,
        organizationId,
      } as unknown as ICreateAccountDto);
    },
    onSuccess: () => {
      handleBackAction();
      snackbarHandler.success(
        isUpdatingAccount ? MESSAGES.ACCOUNT.UPDATED : MESSAGES.ACCOUNT.CREATED,
      );
    },
    onError: (err: unknown) => {
      snackbarHandler.error(getSnackbarApiErrorMessage(err));
    },
  });

  const isDisabled = isDeactivated || isPending || hasReadOnlyAccess();

  return (
    <FormContainer
      control={control}
      extraButtons={
        entity.id && entity.isMaster
          ? [
              {
                onClick: () => {
                  navigate(NESTED_ROUTES.ADMINISTRATION.ALLOCATION_TEMPLATE);
                },
                label: BUTTON_TEXT.goToAllocations,
              },
            ]
          : undefined
      }
      handleBackAction={handleBackAction}
      handleSubmit={handleSubmit((data) => accountMutation(data))}
      header="Account Information"
      isDeactivated={isDeactivated}
      isDirty={isDirty || historyConfigs.isDirty}
      isNew={!entity.id}
      isPending={isPending}
      mainFieldsConfig={formFieldsConfig}
      afterMainFields={
        entity?.id && (
          <>
            <AssociatedMeters
              form="account"
              docId={entity.id}
              isDisabled={isDisabled}
            />
            <HistoricalValuesDialogTable
              historyConfigs={historyConfigs}
              targetHeaderName="Rate Schedule"
              targetColumnType="string"
            />
          </>
        )
      }
    />
  );
};

export default AccountForm;
